import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, ArrowLeft, CheckCircle2, Circle, Download } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-transparent border border-paper/20 rounded-none px-4 py-3.5 text-sm text-paper placeholder:text-paper/30 focus:outline-none focus:border-brasslight transition-colors duration-300";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const { t } = useLanguage();
  const [token, setToken] = useState(() => localStorage.getItem("ogv_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState(null);

  const loadQuotes = useCallback(async (tk) => {
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${tk}` } });
      setQuotes(data.quotes);
    } catch {
      localStorage.removeItem("ogv_token");
      setToken(null);
    }
  }, []);

  useEffect(() => {
    if (token) loadQuotes(token);
  }, [token, loadQuotes]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("ogv_token", data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("admin.error"));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("ogv_token");
    setToken(null);
    setQuotes(null);
  };

  const toggleStatus = async (q) => {
    const next = q.status === "done" ? "new" : "done";
    try {
      await axios.patch(`${API}/admin/quotes/${q.quote_id}/status`, { status: next }, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(quotes.map((x) => (x.quote_id === q.quote_id ? { ...x, status: next } : x)));
      toast.success(next === "done" ? t("admin.markedDone") : t("admin.reopened"));
    } catch {
      toast.error(t("admin.updateError"));
    }
  };

  const download = async (q, a) => {
    try {
      const resp = await axios.get(`${API}/admin/files/${a.storage_path}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("admin.updateError"));
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-midnight flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-md border border-paper/10 p-10 lg:p-12">
          <p className="text-[11px] uppercase tracking-[0.35em] text-brasslight mb-3">{t("admin.space")}</p>
          <h1 className="font-serif font-light text-3xl text-paper mb-10">{t("admin.login")}</h1>
          <div className="space-y-6">
            <input data-testid="admin-input-email" type="email" required placeholder={t("admin.email")} className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
            <input data-testid="admin-input-password" type="password" required placeholder={t("admin.password")} className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p data-testid="admin-login-error" className="mt-5 text-sm text-red-400">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-paper text-midnight text-[11px] uppercase tracking-[0.25em] px-8 py-4 hover:bg-brasslight transition-colors duration-300 disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin" />}
            {t("admin.submit")}
          </button>
          <Link to="/" data-testid="admin-back-home" className="mt-8 inline-flex items-center gap-2 text-xs text-paper/40 hover:text-paper/70 transition-colors duration-300">
            <ArrowLeft size={13} /> {t("admin.back")}
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-paper text-ink">
      <header className="border-b border-ink/5 bg-paper/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
          <p className="font-serif text-xl">{t("admin.titleA")} <span className="text-brass">{t("admin.titleB")}</span></p>
          <div className="flex items-center gap-6">
            <button
              data-testid="admin-export-csv"
              onClick={async () => {
                try {
                  const resp = await axios.get(`${API}/admin/quotes/export`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
                  const url = URL.createObjectURL(resp.data);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = "demandes-devis.csv";
                  link.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error(t("admin.updateError"));
                }
              }}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink/50 hover:text-brass transition-colors duration-300"
            >
              <Download size={13} /> {t("adminExport")}
            </button>
            <Link to="/" data-testid="admin-link-home" className="text-[11px] uppercase tracking-[0.22em] text-ink/50 hover:text-ink transition-colors duration-300">{t("admin.viewSite")}</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink/50 hover:text-red-600 transition-colors duration-300">
              <LogOut size={13} /> {t("admin.logout")}
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
        {quotes === null ? (
          <div className="flex justify-center py-24"><Loader2 size={22} className="animate-spin text-brass" /></div>
        ) : quotes.length === 0 ? (
          <p data-testid="admin-empty-state" className="text-center py-24 text-ink/50 font-serif text-2xl font-light">{t("admin.empty")}</p>
        ) : (
          <div className="space-y-px bg-ink/10 border border-ink/10">
            {quotes.map((q) => (
              <article key={q.quote_id} data-testid={`admin-quote-${q.quote_id}`} className="bg-paper p-6 lg:p-8 grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-3">
                  <p className="font-serif text-xl text-ink">{q.full_name}</p>
                  <p className="text-xs text-ink/45 mt-1">{formatDate(q.created_at)}</p>
                  <p className="text-xs mt-3">
                    {q.status === "done"
                      ? <span className="text-emerald-700 uppercase tracking-[0.2em] text-[10px]">{t("admin.done")}</span>
                      : <span className="text-brass uppercase tracking-[0.2em] text-[10px]">{t("admin.new")}</span>}
                  </p>
                </div>
                <div className="lg:col-span-3 text-sm text-ink/70 space-y-1.5">
                  <p>{q.email}</p>
                  {q.phone && <p>{q.phone}</p>}
                  <p className="text-ink/50">{q.service}</p>
                  <p className="text-ink/50">{q.languages}</p>
                </div>
                <div className="lg:col-span-5">
                  <p className="text-sm leading-relaxed text-ink/70 whitespace-pre-line">{q.message}</p>
                  {q.attachments?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-ink/40 mb-2.5">{t("admin.attachments")}</p>
                      <div className="flex flex-wrap gap-2">
                        {q.attachments.map((a, i) => (
                          <button
                            key={i}
                            data-testid={`admin-download-${q.quote_id}-${i}`}
                            onClick={() => download(q, a)}
                            className="inline-flex items-center gap-2 text-xs border border-ink/15 px-3 py-2 text-ink/60 hover:border-brass hover:text-brass transition-colors duration-300"
                          >
                            <Download size={12} /> {a.filename}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="lg:col-span-1 flex lg:justify-end items-start">
                  <button
                    data-testid={`admin-toggle-${q.quote_id}`}
                    onClick={() => toggleStatus(q)}
                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ink/50 hover:text-brass transition-colors duration-300"
                  >
                    {q.status === "done" ? <CheckCircle2 size={15} className="text-emerald-700" /> : <Circle size={15} />}
                    {q.status === "done" ? t("admin.reopen") : t("admin.markDone")}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
